/**
 * Specialty feature-flag hook.
 *
 * rev v11.0.0 · Phase 2 / C4
 *
 * THE RULE
 * -------
 * The new experience is OFF unless the API says it is ON. A failure — the
 * API is down, the flag is unknown, the response is malformed — leaves the
 * clinician on the interface they already know.
 *
 * That is deliberate and it is the whole point: the rollout is reversible in
 * seconds because turning it off is the default and turning it on is the
 * exceptional case.
 */
import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { flagKeyFor } from "@/lib/specialty";

/** The state of one flag. */
export interface SpecialtyFlagState {
  /** True when the new experience is enabled for this caller. */
  enabled: boolean;
  /** True while the value is being fetched. */
  loading: boolean;
  /** True when the API could not be reached. */
  degraded: boolean;
  /** Refresh the value. */
  refresh: () => void;
}

/**
 * Evaluate the feature flag of a specialty.
 *
 * @param specialtyId the canonical slug or a legacy code
 */
export function useSpecialtyFlag(specialtyId: string | null | undefined): SpecialtyFlagState {
  const key = flagKeyFor(specialtyId);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(Boolean(key));
  const [degraded, setDegraded] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!key) {
      setEnabled(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiFetch<{ key: string; enabled: boolean }>(`/flags/${encodeURIComponent(key)}`)
      .then((res) => {
        if (cancelled) return;
        setEnabled(res?.enabled === true);
        setDegraded(false);
      })
      .catch(() => {
        if (cancelled) return;
        // The API is unreachable or the flag is unknown: stay on the
        // interface the clinician knows.
        setEnabled(false);
        setDegraded(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, nonce]);

  return { enabled, loading, degraded, refresh };
}

/**
 * The whole rollout, as seen by the caller.
 *
 * Used by the adoption dashboard.
 */
export interface RolloutSummary {
  total: number;
  enabled: number;
  flags: Array<{
    key: string;
    specialty: string;
    label: string;
    enabled: boolean;
    rolloutTenants: number;
  }>;
  loading: boolean;
  degraded: boolean;
}

export function useSpecialtyRollout(): RolloutSummary {
  const [state, setState] = useState<RolloutSummary>({
    total: 0,
    enabled: 0,
    flags: [],
    loading: true,
    degraded: false,
  });

  useEffect(() => {
    let cancelled = false;
    apiFetch<RolloutSummary>("/flags")
      .then((res) => {
        if (cancelled) return;
        setState({
          total: res?.total ?? 0,
          enabled: res?.enabled ?? 0,
          flags: res?.flags ?? [],
          loading: false,
          degraded: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, degraded: true }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
